import { Upload } from "lucide-react";
import React, { useCallback, useRef, useState } from "react";

import { cn } from "@/lib/utils";

interface Props {
  onFiles: (files: File[]) => void;
  disabled?: boolean;
  accept?: string;
}

export function FileDropzone({
  onFiles,
  disabled = false,
  accept = ".xml,application/xml,text/xml",
}: Props): React.ReactElement {
  const [isOver, setIsOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (disabled || !files || files.length === 0) return;
      const array = Array.from(files);
      if (array.length > 0) onFiles(array);
    },
    [disabled, onFiles],
  );

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!isOver && !disabled) setIsOver(true);
  };

  const onDragLeave = () => setIsOver(false);

  return (
    <div
      className={cn(
        "relative flex cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-8 transition-colors",
        isOver
          ? "border-primary bg-primary/5"
          : "border-input hover:border-primary/50 hover:bg-accent/30",
        disabled && "pointer-events-none opacity-50",
      )}
      onClick={() => inputRef.current?.click()}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          inputRef.current?.click();
        }
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple
        className="hidden"
        onChange={(e) => {
          handleFiles(e.currentTarget.files);
          // reset so the same file can be re-selected
          e.currentTarget.value = "";
        }}
      />
      <Upload className="size-10 text-muted-foreground" aria-hidden />
      <div className="text-center">
        <div className="text-sm font-medium">
          Перетащите один или несколько XML файлов сюда или нажмите для выбора
        </div>
        <div className="text-xs text-muted-foreground">
          Принимаются файлы ФНС УПД 5.03 (.xml), максимум 10 МБ каждый
        </div>
      </div>
    </div>
  );
}
